import { useNewCode, useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { useDropdown } from '@calendly/ui/components/dropdown'
import { IconButton } from '@calendly/ui/components/icon-button'
import { SettingsMenuVerticalIcon } from '@calendly/ui/components/icons'
import moment from 'moment'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence'
import { DeleteRecapsModal } from 'features/tracked_meetings'
import { useFetchTrackedMeetingByForeignIdQuery } from 'hooks/tracked_meetings/api'
import { timezoneSelector } from 'selectors/current_user'
import { isCalendlyMeeting } from 'shared/utils/meeting_helpers'

import { useNotetaker } from './add_notetaker_button'
import { CancelMeetingModal } from './cancel_meeting_modal'
import { MoreActionsMenu } from './more_actions_menu'
import { isPendingEvent } from '../utils'

export const MoreActions = ({ event, showReportModal, clearSelectedEvent, updateSelectedEvent, inProgress }) => {
  const eventType = event.eventType
  const [toggle, Dropdown, isExpanded] = useDropdown()
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useBooleanState(false)
  const [isDeleteRecapModalOpen, openDeleteRecapModal, closeDeleteRecapModal] = useBooleanState(false)
  const { canAdd: canAddNotetaker } = useNotetaker()
  const { data: currentUser } = useGetUserQuery()
  const isNotPretending = currentUser && !currentUser.is_pretending
  const timezone = useSelector(timezoneSelector)
  const hasStarted = moment().tz(timezone).isAfter(moment(event.startTime).tz(timezone))

  const isMeetingIntelligenceEnabled = useNewCode('meeting_intelligence')?.enabled
  const { data: meetingIntelligence } = useFetchTrackedMeetingByForeignIdQuery(
    event?.uuid,
    { skip: !event || isPendingEvent(event) || !isMeetingIntelligenceEnabled }
  )
  const { data: meetingIntelligenceSettings } = useGetMeetingIntelligenceUserSettingsQuery({})

  const shouldShowRemoveNotetakerItem = isNotPretending
    && !!meetingIntelligenceSettings?.miEnabled
    && meetingIntelligence?.enabled
    && ['Scheduled'].includes(meetingIntelligence?.status)
  const shouldShowCancelItem = event.policy?.cancel && isNotPretending && (canAddNotetaker || hasStarted)
  const shouldShowDeleteItem = Boolean(meetingIntelligence?.trackedMeetingUuid)
    && meetingIntelligence?.status === 'Completed'
    && !meetingIntelligence?.deleted
  const featureFlagEnabled = useFlag('trust_and_safety_reactive_block')
  const shouldShowReportItem = !event?.past && isCalendlyMeeting(event) && !featureFlagEnabled
  const shouldHideBookFollowUpMeeting = eventType?.deleted
    || eventType?.type === 'AdhocEventType'
    || event.modified
    || eventType?.kind !== 'solo'
    || (event.kind && event.kind !== 'CALENDLY')
    || !inProgress
  const shouldShowMenu = [
    shouldShowRemoveNotetakerItem,
    shouldShowCancelItem,
    shouldShowDeleteItem,
    shouldShowReportItem,
    !shouldHideBookFollowUpMeeting
  ].some(Boolean)

  if (!shouldShowMenu) return null

  return (
    <>
      <IconButton aria-label={I18n.t('publisher.meetings.actions.more_actions')} onClick={toggle} aria-expanded={isExpanded}>
        <SettingsMenuVerticalIcon />
      </IconButton>
      <Dropdown>
        <MoreActionsMenu
          event={event}
          isExpanded={isExpanded}
          meetingIntelligence={meetingIntelligence}
          openCancelModal={openCancelModal}
          openDeleteRecapModal={openDeleteRecapModal}
          shouldShowBookFollowUpMeetingItem={!shouldHideBookFollowUpMeeting}
          shouldShowCancelItem={shouldShowCancelItem}
          shouldShowDeleteItem={shouldShowDeleteItem}
          shouldShowMenu={shouldShowMenu}
          shouldShowRemoveNotetakerItem={shouldShowRemoveNotetakerItem}
          shouldShowReportItem={shouldShowReportItem}
          showReportModal={showReportModal}
          toggleDropdown={toggle}
        />
      </Dropdown>
      {isCancelModalOpen && (
        <CancelMeetingModal
          event={event}
          onClose={closeCancelModal}
          clearSelectedEvent={clearSelectedEvent}
          updateSelectedEvent={updateSelectedEvent}
          showReportModal={showReportModal}
        />
      )}
      {shouldShowDeleteItem && (
        <DeleteRecapsModal
          isOpen={isDeleteRecapModalOpen}
          meetingUuids={[meetingIntelligence.trackedMeetingUuid]}
          onClose={closeDeleteRecapModal}
          onSuccess={closeDeleteRecapModal}
        />
      )}
    </>
  )
}
